import React from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import currency from 'currency.js';

/**
 * The product screen's buy bar on phones.
 *
 * The in-panel Personalize button is hidden below 640px and this carries it
 * instead, so the action is reachable without scrolling back up past the
 * description and the details.
 *
 * ⚠️ IT OWNS THE FIXED SLOT ON A PRODUCT SCREEN, and the order flow's
 * page-level CheckoutBar stands down on a phone while it is up. Two fixed
 * bars stacked on a phone eat a third of the screen, and of the two this is the
 * one the customer came to the product for; Continue to Cart is one tap away on
 * the catalog behind it.
 */
export default function StickyBuyBar({
  productName, unitPrice, quantity, isLoading, onPersonalize,
}) {
  // The LIVE total, recomputed as the stepper moves — the number a customer
  // checks before committing, and the reason this shows a total rather than the
  // unit price the panel above already states.
  const total = currency(unitPrice).multiply(quantity);

  return (
    <div
      className={cn(
        'fixed inset-x-0 bottom-0 z-50 flex items-center gap-3.5 border-0 border-t',
        'border-solid border-gift-line bg-white px-4 pt-3 sm:hidden',
        'shadow-[0_-6px_24px_rgba(36,48,64,.08)]',
      )}
      // Home-indicator inset, so the bar clears the gesture bar on a modern
      // phone instead of sitting under it.
      style={{ paddingBottom: 'calc(0.75rem + env(safe-area-inset-bottom))' }}
    >
      <div className="flex min-w-0 flex-col leading-tight">
        <span className="text-lg font-extrabold text-gift-ink">{total.format()}</span>
        <span className="truncate text-xs text-gift-inkSoft">
          {`${productName} · qty ${quantity}`}
        </span>
      </div>

      <button
        type="button"
        disabled={isLoading}
        onClick={onPersonalize}
        className={cn(
          'flex flex-1 items-center justify-center rounded-gift-pill border-0 px-5 py-3.5',
          'text-[15.5px] font-extrabold text-white transition',
          isLoading ? 'cursor-wait bg-gray-300' : 'bg-gift-accent hover:bg-gift-accentHover',
        )}
      >
        {isLoading ? 'Adding…' : 'Personalize'}
      </button>
    </div>
  );
}

StickyBuyBar.defaultProps = {
  unitPrice: 0,
  quantity: 1,
  isLoading: false,
};

StickyBuyBar.propTypes = {
  productName: PropTypes.string.isRequired,
  unitPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  quantity: PropTypes.number,
  isLoading: PropTypes.bool,
  onPersonalize: PropTypes.func.isRequired,
};
